/**
 * Admin Panel Aggregated API Service
 * 
 * Composes domain-specific APIs into a unified adminAPI object
 * for seamless backward compatibility across all admin pages.
 */

import { api } from './client';
import { courierAPI } from './couriers';
import { fulfillmentAPI } from './fulfillment';
import { marketingAPI } from './marketing';

export const adminAPI = {
  // Dashboard
  getDashboardStats: async (): Promise<any> => {
    const response = await api.get('/admin/dashboard/stats/');
    return response.data;
  },
  getDashboardCharts: async (startDate?: string, endDate?: string): Promise<any> => {
    const params: Record<string, string> = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await api.get('/admin/dashboard/charts/', { params });
    return response.data;
  },

  // Products
  getProducts: async () => api.get('/admin/products/'),
  getInventorySummary: async () => api.get('/admin/products/inventory_summary/'),
  createProduct: async (data: any) => {
    const config = data instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
    return api.post('/admin/products/', data, config);
  },
  updateProduct: async (id: number, data: any) => {
    const config = data instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
    return api.put(`/admin/products/${id}/`, data, config);
  },
  deleteProduct: async (id: number) => api.delete(`/admin/products/${id}/`),
  uploadProductImages: async (productId: number, files: File[]) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('images', file));
    return api.post(`/admin/products/${productId}/upload_images/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteProductImage: async (productId: number, imageId: number) => {
    return api.delete(`/admin/products/${productId}/images/${imageId}/`);
  },
  getProductImages: async (productId: number) => {
    return api.get(`/admin/products/${productId}/`);
  },

  // Categories
  getCategories: async () => api.get('/admin/categories/'),
  createCategory: async (data: any) => api.post('/admin/categories/', data),
  updateCategory: async (id: number, data: any) => api.put(`/admin/categories/${id}/`, data),
  deleteCategory: async (id: number) => api.delete(`/admin/categories/${id}/`),

  // Orders
  getOrders: async (params?: any) => api.get('/orders/', { params }),
  updateOrderStatus: async (id: number, status: string) => api.post(`/orders/${id}/update_status/`, { status }),
  updateOrder: async (id: number, data: any) => api.post(`/orders/${id}/edit/`, data),
  createManualOrder: async (data: any) => api.post('/orders/create_manual/', data),
  bulkImportOrders: async (payload: FormData | Record<string, any>) => {
    if (payload instanceof FormData) {
      return api.post('/orders/bulk_import/', payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    return api.post('/orders/bulk_import/', payload);
  },
  downloadImportTemplate: async (format: 'xlsx' | 'csv' = 'xlsx') =>
    api.get(`/orders/import_template/?file_format=${format}`, { responseType: 'blob' }),
  applyOrderDiscount: async (id: number, discountType: string | null, discountValue: number | null) =>
    api.post(`/orders/${id}/apply_discount/`, { discount_type: discountType, discount_value: discountValue }),
  deleteOrder: async (id: number) => api.delete(`/orders/${id}/`),
  bulkDeleteOrders: async (orderIds: number[]) => api.post('/orders/bulk_delete/', { order_ids: orderIds }),

  // Courier logistics delegates
  ...courierAPI,

  // Warehouse fulfillment delegates
  ...fulfillmentAPI,

  // Marketing, Tracking & Ads delegates
  ...marketingAPI,

  // Customers
  getCustomers: async () => api.get('/customers/'),

  // Reviews
  getReviews: async () => api.get('/reviews/'),
  approveReview: async (id: number) => api.post(`/reviews/${id}/approve/`),
  rejectReview: async (id: number) => api.post(`/reviews/${id}/reject/`),
  deleteReview: async (id: number) => api.delete(`/reviews/${id}/`),

  // Coupons
  getCoupons: async () => api.get('/coupons/'),
  createCoupon: async (data: any) => api.post('/coupons/', data),
  updateCoupon: async (id: number, data: any) => api.put(`/coupons/${id}/`, data),
  deleteCoupon: async (id: number) => api.delete(`/coupons/${id}/`),

  // Delivery Options
  getDeliveryOptions: async () => api.get('/admin/delivery-options/'),
  createDeliveryOption: async (data: any) => api.post('/admin/delivery-options/', data),
  updateDeliveryOption: async (id: number, data: any) => api.put(`/admin/delivery-options/${id}/`, data),
  deleteDeliveryOption: async (id: number) => api.delete(`/admin/delivery-options/${id}/`),

  // Content & SEO
  getSEO: async () => api.get('/admin/seo/'),
  updateSEO: async (data: any) => api.post('/admin/seo/', data),
  getContent: async () => api.get('/admin/content/'),
  updateContent: async (type: string, data: any) => api.post(`/admin/content/${type}/`, data),

  // Banners
  getBanners: () => api.get('/admin/banners/'),
  createBanner: (data: any) =>
    api.post('/admin/banners/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  updateBanner: (id: number, data: any) =>
    api.put(`/admin/banners/${id}/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  deleteBanner: (id: number) => api.delete(`/admin/banners/${id}/`),

  // Translations
  getTranslations: async () => api.get('/admin/translations/'),
  updateTranslation: async (id: number, data: any) => api.put(`/admin/translations/${id}/`, data),
  importTranslations: async (data: any) => api.post('/admin/translations/import/', data),
  exportTranslations: async () => api.get('/admin/translations/export/'),
};
